/**
 * Pure rules ported from the console FNOL page
 * (`apps/console/src/console/customers/claims/fnol/fnolRules.ts` and
 * `FnolFieldControl.tsx`) for the `ClaimReport` A2UI card. Ported rather than
 * imported: the FNOL page lives in `apps/console` and this card lives in
 * `libs/ui`, and the contract asks for these as standalone helpers so they
 * can be unit-tested directly without dragging in the FNOL page's types.
 *
 * `conditionMatches` / `visibleFields` must keep matching the server's
 * `FnolCondition#matches` exactly, the same requirement fnolRules.ts states —
 * the server always re-checks on submit, so this only drives what the card
 * shows.
 */

export type ClaimReportConditionOp =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'IN'
  | 'ANY_EXCEPT';

export interface ClaimReportCondition {
  readonly field: string;
  readonly op: ClaimReportConditionOp;
  readonly values: readonly string[];
}

export type ClaimReportFieldType =
  | 'RADIO'
  | 'SELECT'
  | 'BOOLEAN'
  | 'CHECKS'
  | 'DATE'
  | 'DATETIME';

export interface ClaimReportOption {
  readonly code: string;
  readonly label: string;
}

export interface ClaimReportField {
  readonly key: string;
  readonly step: string;
  readonly group: string;
  readonly label: string;
  readonly type: ClaimReportFieldType;
  readonly required: boolean;
  readonly options: readonly ClaimReportOption[];
  readonly showIf: ClaimReportCondition | null;
  readonly hint: string | null;
  readonly allowFuture: boolean;
  readonly situationIds: readonly string[] | null;
}

export type ClaimReportAnswers = Record<string, unknown>;

/** The order the card presents field steps in — matches FNOL_STEP_ORDER. */
export const CLAIM_REPORT_STEP_ORDER: readonly string[] = [
  'report',
  'contact',
  'details',
  'notices',
];

/**
 * Same four operators as the server's condition matcher (and fnolRules'
 * `conditionMatches`). Compares against the string form of the answer — a
 * stored boolean `false` matches `EQUALS(field, "false")` — and ANY_EXCEPT
 * only matches an answer that is itself a list.
 */
export function conditionMatches(
  condition: ClaimReportCondition,
  context: ClaimReportAnswers
): boolean {
  const answer = context[condition.field];
  const asString =
    answer === undefined || answer === null ? null : String(answer);
  switch (condition.op) {
    case 'EQUALS':
      return asString !== null && condition.values[0] === asString;
    case 'NOT_EQUALS':
      return asString === null || condition.values[0] !== asString;
    case 'IN':
      return asString !== null && condition.values.includes(asString);
    case 'ANY_EXCEPT':
      return (
        Array.isArray(answer) &&
        answer.some((item) => !condition.values.includes(String(item)))
      );
    default:
      return false;
  }
}

/** Whether `field` should be shown for the given situation: `situationIds`
 *  null means every situation, otherwise only when the current situation is
 *  in the list. */
export function situationAllows(
  field: ClaimReportField,
  situationId: string | undefined
): boolean {
  if (field.situationIds === null) return true;
  return situationId !== undefined && field.situationIds.includes(situationId);
}

/**
 * `context` is what `showIf` is evaluated against, with `situationId` always
 * added — mirroring the server and fnolRules' `visibleFields`. A field
 * hidden by `showIf`, or not offered for the current situation, is dropped.
 */
export function visibleFields(
  fields: readonly ClaimReportField[],
  context: ClaimReportAnswers,
  situationId: string | undefined
): ClaimReportField[] {
  const ctx: ClaimReportAnswers = { ...context, situationId };
  return fields.filter(
    (field) =>
      situationAllows(field, situationId) &&
      (field.showIf === null || conditionMatches(field.showIf, ctx))
  );
}

/** Blank strings and empty lists count as missing; a boolean `false` and a
 *  number `0` are answers, not gaps — same rule as fnolRules' `isBlank`. */
export function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Keys in `answers` for a field that is no longer visible under `fields` for
 * `situationId` — a field hidden by `showIf` or a situation change after
 * having been answered. `ClaimReport:SendClaimChoices` reads `/answers`
 * verbatim (a literal path binding, not filtered at send time), so the card
 * must actively drop a hidden field's stale answer as soon as it hides,
 * rather than only at Send — otherwise a pick made before a situation change
 * (or before another answer hid the field) would still ride along into the
 * chat-send message. `situationId` itself is not a field and is never
 * dropped this way.
 */
export function hiddenAnswerKeys(
  fields: readonly ClaimReportField[],
  answers: ClaimReportAnswers,
  situationId: string | undefined
): string[] {
  // Only ever drop a key that names one of `fields` — an answer entry with no
  // corresponding field (situationId itself, or anything else the card
  // doesn't know about) is left untouched rather than guessed at.
  const knownKeys = new Set(fields.map((field) => field.key));
  const visibleKeys = new Set(
    visibleFields(fields, answers, situationId).map((field) => field.key)
  );
  return Object.keys(answers).filter(
    (key) =>
      knownKeys.has(key) && !visibleKeys.has(key) && !isBlank(answers[key])
  );
}

/** Whether at least one visible-field (or situation) answer has been picked —
 *  the only gate `ClaimReport:SendClaimChoices` observes; required fields are
 *  not enforced by the card. */
export function hasAnyAnswer(answers: ClaimReportAnswers): boolean {
  return Object.keys(answers).some((key) => !isBlank(answers[key]));
}

/** Renders one answer the way the user picked it, using the field's option
 *  labels for RADIO/SELECT/CHECKS/BOOLEAN — for the frozen "here's what you
 *  sent" summary, so it reads like the card the user saw, not raw codes. */
export function formatAnswerLabel(
  field: ClaimReportField,
  value: unknown
): string {
  if (isBlank(value)) return '';
  if (field.type === 'BOOLEAN') {
    return value === true || value === 'true' ? 'Yes' : 'No';
  }
  const labelFor = (code: string): string =>
    field.options.find((option) => option.code === code)?.label ?? code;
  if (field.type === 'CHECKS' && Array.isArray(value)) {
    return value.map((entry) => labelFor(String(entry))).join(', ');
  }
  if (
    (field.type === 'RADIO' || field.type === 'SELECT') &&
    typeof value === 'string'
  ) {
    return labelFor(value);
  }
  return String(value);
}

export interface ClaimReportFieldGroup {
  readonly step: string;
  readonly group: string;
  readonly fields: readonly ClaimReportField[];
}

/** Buckets fields into step/group sections, in `CLAIM_REPORT_STEP_ORDER` and,
 *  within a step, in first-appearance order — matches fnolRules'
 *  `groupFields`. A field in a step outside the known order is dropped. */
export function groupFields(
  fields: readonly ClaimReportField[]
): ClaimReportFieldGroup[] {
  const buckets = new Map<string, ClaimReportFieldGroup>();
  fields.forEach((field) => {
    if (!CLAIM_REPORT_STEP_ORDER.includes(field.step)) return;
    const key = `${field.step}::${field.group}`;
    const existing = buckets.get(key);
    if (existing) {
      (existing.fields as ClaimReportField[]).push(field);
      return;
    }
    buckets.set(key, { step: field.step, group: field.group, fields: [field] });
  });
  return Array.from(buckets.values()).sort(
    (a, b) =>
      CLAIM_REPORT_STEP_ORDER.indexOf(a.step) -
      CLAIM_REPORT_STEP_ORDER.indexOf(b.step)
  );
}

/** The distinct field steps present in `fields`, in first-appearance order —
 *  what the card's per-screen wizard steps through. */
export function stepsFor(fields: readonly ClaimReportField[]): string[] {
  const seen: string[] = [];
  fields.forEach((field) => {
    if (
      CLAIM_REPORT_STEP_ORDER.includes(field.step) &&
      !seen.includes(field.step)
    ) {
      seen.push(field.step);
    }
  });
  return seen.sort(
    (a, b) =>
      CLAIM_REPORT_STEP_ORDER.indexOf(a) - CLAIM_REPORT_STEP_ORDER.indexOf(b)
  );
}

/** Choices that stand alone ("No one yet", "Not sure"); picking anything else
 *  drops them, and picking one of them drops everything else. Must match the
 *  server's exclusion set — see FnolFieldControl's EXCLUSIVE_CODES, which
 *  itself must match fir-claimsmgmt's catalog/FnolField.java EXCLUSIVE
 *  constant. */
export const EXCLUSIVE_CHECKS_CODES = new Set([
  'NONE',
  'NOTHING_YET',
  'NOT_SURE',
]);

/**
 * Applies a CHECKS click: unchecking always just removes the code. Checking
 * an exclusive code replaces the whole selection; checking a normal code
 * drops any exclusive code already selected, since the two can't both be
 * true. Ported verbatim from FnolFieldControl's `toggleChoice`, using
 * `Array.from` rather than a spread of a Set — under this project's jest
 * config (target es5 in tests), `[...set]` yields `[]`.
 */
export function toggleChoice(
  current: readonly string[],
  code: string,
  checked: boolean
): string[] {
  if (!checked) {
    return current.filter((selected) => selected !== code);
  }
  if (EXCLUSIVE_CHECKS_CODES.has(code)) {
    return [code];
  }
  return Array.from(
    current.filter((selected) => !EXCLUSIVE_CHECKS_CODES.has(selected))
  ).concat(code);
}

/** Today as a local "YYYY-MM-DD" string, for the DATE control's "Today" chip
 *  and its default `maxDate`. */
export function todayIsoDate(): string {
  const now = new Date();
  const localMs = now.getTime() - now.getTimezoneOffset() * 60_000;
  return new Date(localMs).toISOString().slice(0, 10);
}

/** Yesterday as a local "YYYY-MM-DD" string, for the DATE control's
 *  "Yesterday" chip. */
export function yesterdayIsoDate(): string {
  const now = new Date();
  const localMs = now.getTime() - now.getTimezoneOffset() * 60_000 - 86_400_000;
  return new Date(localMs).toISOString().slice(0, 10);
}

/** The "YYYY-MM-DD" ceiling a DatePickerInput's `maxDate` should carry, or
 *  `undefined` when the field allows a future date. Mantine's date value type
 *  is a plain string (`DateStringValue`), not a `Date` object, so this stays
 *  a string too rather than needing a round trip through `Date`. */
export function maxDateFor(field: ClaimReportField): string | undefined {
  return field.allowFuture ? undefined : todayIsoDate();
}

/** Half-hour time-of-day chips for the DATETIME control's time picker, e.g.
 *  "00:00".."23:30" — a fixed chip list rather than a text/native time input,
 *  since a TimeInput can render as a textbox under jsdom and this card is
 *  choices-only. */
export function halfHourTimes(): string[] {
  const out: string[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    out.push(`${String(hour).padStart(2, '0')}:00`);
    out.push(`${String(hour).padStart(2, '0')}:30`);
  }
  return out;
}

/** Combines a "YYYY-MM-DD" date and "HH:mm" time into the DATETIME answer
 *  shape the contract specifies: "YYYY-MM-DDTHH:mm", local, no offset. */
export function combineDateTime(
  date: string | undefined,
  time: string | undefined
): string | undefined {
  if (!date || !time) return undefined;
  return `${date}T${time}`;
}

/** Splits a combined "YYYY-MM-DDTHH:mm" answer back into its date and time
 *  parts, for prefilling the two controls from an existing answer. */
export function splitDateTime(value: unknown): {
  date?: string;
  time?: string;
} {
  if (typeof value !== 'string' || !value.includes('T')) return {};
  const [date, time] = value.split('T');
  return { date, time };
}
